import { createContext, use, useMemo, type ReactNode } from "react";
import { hasProduct, type ProductCode } from "@packages/core/session/products.ts";
import { isCollaborator, isExecutive, isIndividualContributor, isOrganizationAdmin, isReportingManager, isTeamManager } from "@packages/core/session/roles.ts";
import type { Session } from "@packages/core/session/session.ts";

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ session, children }: { session: Session | null; children: ReactNode }) {
    return <SessionContext value={session}>{children}</SessionContext>;
}

export function useSession() {
    return use(SessionContext);
}

export function useIsAuthenticated() {
    return useSession() !== null;
}

export function useHasProduct(product: ProductCode) {
    return hasProduct(useSession(), product);
}

export function useUserRoles() {
    const session = useSession();

    return useMemo(
        () => ({
            isReportingManager: isReportingManager(session),
            isTeamManager: isTeamManager(session),
            isExecutive: isExecutive(session),
            isCollaborator: isCollaborator(session),
            isOrganizationAdmin: isOrganizationAdmin(session),
            isIndividualContributor: isIndividualContributor(session)
        }),
        [session]
    );
}
